// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

// Internal Dependencies
import { updateModalContent, toggleModal } from '../../actions/modal';
import { getCategories } from '../../actions/admin/category';
import { IsEmpty, IsTrue } from '../../common/checks';
import { formatDateToDayMonthNameYear, showDateInSGTime } from '../../common/util';
import apiService from '../../services.js/apiService';

const PostsTable = (props) => {
  const table = useRef(null);
  const columns = [
    { title: 'Id', field: 'id', filtering: false, width: '5%' },
    { title: 'Page Name', field: 'brand', filtering: false },
    { title: 'Status', field: 'status', filtering: false,  width: '5%' },
    { title: 'Searchable Status', field: 'searchableStatus', filtering: false,  width: '10%' },
    { title: 'Title', field: 'title', filtering: false },
    {
      title:     'Description',
      field:     'description',
      filtering: false,
      render:    (post) => {
        const maxLength = 50;
        if (!IsEmpty(post.description)) {
          return (post.description.length >= maxLength) ? `${post.description.substring(0, maxLength)}...` : post.description;
        }
      },
    },
    {
      title:  'Post Date',
      field:  'postDate',
      render: (post) => (post.postDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(post.postDate))}` : '-'),
    },
    {
      title:  'Start Date',
      field:  'promoFromDate',
      render: (post) => (post.promoFromDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(post.promoFromDate))}` : '-'),
    },
    {
      title:  'End Date',
      field:  'promoTillDate',
      render: (post) => (post.promoTillDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(post.promoTillDate))}` : '-'),
    },
    { title: 'Last Updated By', field: 'lastUpdatedBy' },
  ];

  const initializeFocusValue = {
    pageNameFocus: false,
    postIdFocus:   false,
  };

  const [pageSize, setPageSize] = useState(20);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const { pageNameFocus, postIdFocus } = autoFocus;

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [lastUpdatedByOptions, setLastUpdatedByOptions] = useState([]);

  const [triggerClearFilters, setTriggerClearFilters] = useState(false);
  const [postId, setPostId] = useState('');
  const [pageName, setPageName] = useState('');
  const [status, setStatus] = useState('');
  const [searchableStatus, setSearchableStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');

  async function fetchCategoriesData() {
    const categories = await props.getCategories();
    const options = categories.map((category) => ({
      value: category.id,
      label: category.name,
    }));

    setCategoryOptions(options);
  }

  function searchPageName(event) {
    setPageName(event.target.value);
    setAutoFocus({ pageNameFocus: true });
  }

  function searchPostId(event) {
    setPostId(event.target.value);
    setAutoFocus({ postIdFocus: true });
  }

  const handleChange = (event) => {
    if (event.target.name === 'sortByCategory') {
      setSelectedCategory(event.target.value);
      setCategoryId(event.target.value);
    }

    if (event.target.name === 'postStatus') {
      setStatus(event.target.value);
    }

    if (event.target.name === 'postSearchableStatus') {
      setSearchableStatus(event.target.value);
    }

    if (event.target.name === 'lastUpdatedBy') {
      setLastUpdatedBy(event.target.value);
    }
  };

  const handleDelete = (id) => {
    props.updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this post will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            await apiService.deletePost(id);
            table.current.onQueryChange();
            toast.success('Post deleted');
          } catch (error) {
            toast.error(error.message);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    props.toggleModal();
  };

  const fetchLastUpdatedByOptions = async () => {
    try {
      const staffNames = await apiService.getStaffNames();
      setLastUpdatedByOptions(staffNames);
    } catch (err) {
      console.log(`fetchLastUpdatedByOptions : ${err.message}`);
    }
  };

  const clearFilters = () => {
    setPostId('');
    setPageName('');
    setStatus('');
    setSearchableStatus('');
    setCategoryId('');
    setSelectedCategory('');

    setTriggerClearFilters(true);
  };

  useEffect(() => {
    fetchLastUpdatedByOptions();
  }, []);

  useEffect(() => {
    fetchCategoriesData();
  }, [selectedCategory]);

  useEffect(() => {
    if (IsTrue(triggerClearFilters)) {
      table.current.onQueryChange();
      setTriggerClearFilters(false);
    }
  }, [triggerClearFilters]);

  return (
    <div>
      <MaterialTable
        tableRef={table}
        className="material-table"
        title=""
        columns={columns}
        onChangeRowsPerPage={(size) => {
          setAutoFocus(initializeFocusValue);
          setPageSize(size);
        }}
        data={(query) => new Promise(async (resolve, reject) => {
          try {
            query.id = postId;
            query.brand = pageName;
            query.categoryId = categoryId;
            query.status = status;
            query.searchableStatus = searchableStatus;
            query.lastUpdatedBy = lastUpdatedBy;
            query.pageNo = query.page;

            // SET DEFAULT SORT BY ID
            query.orderBy = 'id';
            query.orderDirection = 'DESC';
            query.order = { col: query.orderBy, dir: query.orderDirection };

            const apiQuery = {
              id:     postId,
              brand:  pageName,
              categoryId,
              status,
              searchableStatus,
              lastUpdatedBy,
              pageNo: query.page,

              // SET DEFAULT SORT BY ID
              orderBy:        'id',
              orderDirection: 'DESC',
              order:          { col: query.orderBy, dir: query.orderDirection },
            };

            const result = await apiService.getPosts(apiQuery);
            let { rows, totalCount } = result;
            let { page } = query;

            if (rows.length === 0) {
              page = 0;

              if (totalCount !== 0) {
                const resultFirstPage = await apiService.getPosts(...query, page);
                rows = resultFirstPage.rows;
              }
            }

            resolve({
              data: rows,
              page,
              totalCount,
            });
          } catch (err) {
            toast.error(err);
            resolve({
              data:       [],
              page:       0,
              totalCount: 0,
            });
          }
        })}
        actions={[
          {
            icon:    'edit',
            tooltip: 'Edit Post',
            onClick: (event, post) => {
              window.open(`/admin/page/${post.pageId}/post/${post.id}`, '_blank');
            },
          },
          {
            icon:    'delete',
            tooltip: 'Delete Post',
            onClick: (event, post) => {
              handleDelete(post.id);
            },
          },
        ]}
        options={{
          pageSize,
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
        }}
        components={{
          Toolbar: () => (
            <div className="pt-3">
              <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <Button variant="outlined" size="medium" color="secondary" onClick={() => { clearFilters(); }}>Clear Filters</Button>
                  </FormControl>
                </Box>
              </Box>

              <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
                <Box p={1} width="5%">
                  <FormControl fullWidth>
                    <TextField
                      label="Id"
                      type="number"
                      value={postId || ''}
                      onChange={searchPostId}
                      autoFocus={postIdFocus}
                    />
                  </FormControl>
                </Box>

                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <TextField
                      label="Page Name"
                      value={pageName || ''}
                      onChange={searchPageName}
                      autoFocus={pageNameFocus}
                    />
                  </FormControl>
                </Box>

                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={status}
                      name="postStatus"
                      onChange={handleChange}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Live">Live</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <InputLabel id="status-filter-label">Searchable Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={searchableStatus}
                      name="postSearchableStatus"
                      onChange={handleChange}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="Live">Live</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Expired">Expired</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {
                  !IsEmpty(lastUpdatedByOptions) && (
                    <Box p={1} width="15%">
                      <FormControl fullWidth>
                        <InputLabel id="status-filter-label">Last Updated By</InputLabel>
                        <Select
                          labelId="status-filter-label"
                          value={lastUpdatedBy}
                          name="lastUpdatedBy"
                          onChange={handleChange}
                        >
                          <MenuItem value="">All</MenuItem>
                          {
                            lastUpdatedByOptions.map((staffName) => (
                              <MenuItem key={staffName} value={staffName}>{staffName}</MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Box>
                  )
                }

                <Box p={1} width="20%">
                  <FormControl fullWidth>
                    <InputLabel id="category-label">Category</InputLabel>
                    <Select
                      id="category-label"
                      value={selectedCategory}
                      name="sortByCategory"
                      onChange={handleChange}
                    >
                      <MenuItem value="">All</MenuItem>
                      {
                        categoryOptions.map((category) => (
                          <MenuItem key={category.value} value={category.value}>{category.label}</MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Box>

                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <Button variant="contained" className="mt-3" size="medium" color="secondary" onClick={() => { table.current.onQueryChange(); }}>Search</Button>
                  </FormControl>
                </Box>
              </Box>
            </div>
          ),
        }}
      />
    </div>
  );
};

PostsTable.propTypes = {
  getCategories:      PropTypes.func.isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

// map props to redux state (use Chrome Redux DevTools to help identify how state are stored)
/*function mapStateToProps(state) {
  return {};
}*/

// map the redux action creators to props
const mapActionToProps = {
  getCategories,
  updateModalContent,
  toggleModal,
};

// connect(mapStateToProps, actions to component as props)(component)
export default connect(
  null,
  mapActionToProps,
)(PostsTable);
